import MarkPaymentAsPaidDialog from "@components/dialogs/mark_payment_as_paid"
import RetryPaymentDialog from "@components/dialogs/retry_payment"
import GridTable from "@components/grid-table"
import { DELETE_COLOR, ORANGISH, WATSAPP_ACTIVE } from "@constants/colors"
import { useCustomerPageContext } from "@contexts/customer"
import {
  getCustomerInvoicesQuery,
  retryPaymentMutation,
  updateCustomerInvoiceMutation
} from "@domain/customer/infos"
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material"
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import PopperMenu from "@pages/customer/infos/tabs/users/popper_menu"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import {
  CustomerInvoice,
  CustomerInvoicesPaginatedData,
  PaymentDetailsEnum,
  PaymentMethodEnum,
  PaymentStatus
} from "__generated__/graphql"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import styles from "./styles"

export type InvoiceData = {
  _id: string
  paymentNumber: string
  amount: string
  amountPaid: number
  paymentMethod: string
  status: string
  paymentStatus: string
  invoice?: string
  isDefaultInvoice?: boolean
  date: string
}

export type UpdateInvoiceData = {
  status?: PaymentStatus
  paymentInvoice?: File
  paymentMethod?: string
  amountPaid?: number
}

export type DataTableRowData = {
  _id: string
  invoiceNo: string
  amountPaid: string
  paymentStatus: PaymentStatus
  paymentMethod: string
  paymentInvoice: string
  createdAt: string
  isDefaultInvoice: boolean
}

export default function CustomersPageInvoices() {
  const { translate, isLoading } = useCustomerPageContext()

  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 20
  })

  const paymentMethodMap = {
    CREDIT_CARD: translate("payment_method_credit_card"),
    BANK_TRANSFER: translate("payment_method_bank_transfer"),
    CASH: translate("payment_method_cash")
  }

  const { id } = useParams()

  const [optionAnchorEl, setOptionAnchorEl] =
    useState<HTMLButtonElement | null>(null)
  const [customerInvoiceData, setCustomerInvoiceData] =
    useState<CustomerInvoicesPaginatedData>()
  const [currentInvoice, setCurrentInvoice] = useState<InvoiceData>()

  const [isPaymentOpened, setIsPaymentOpened] = useState<boolean>(false)

  const [isRetryPaymentOpened, setIsRetryPaymentOpened] =
    useState<boolean>(false)

  const [ip, setIP] = useState<string>("")

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const handlePopClose = () => {
    setOptionAnchorEl(null)
  }

  const handleOptionClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    params: GridRenderCellParams<InvoiceData>
  ) => {
    setOptionAnchorEl(event.currentTarget)

    setCurrentInvoice(params.row)
  }

  function handleUploadInvoice() {
    if (!fileInputRef.current) {
      return
    }
    fileInputRef.current.click()
  }

  const handleUpdateInvoice = async (input: UpdateInvoiceData) => {
    if (!currentInvoice?._id || !id) {
      return
    }

    const { isSuccess, errorMessage } = await updateCustomerInvoiceMutation({
      _id: currentInvoice._id,
      customerId: id,
      ...(input.paymentInvoice && {
        paymentInvoice: input.paymentInvoice
      }),
      ...(input.status && {
        paymentStatus: input.status
      }),
      ...(input.status && {
        paymentStatus: input.status
      }),
      ...(input.amountPaid && {
        amountPaid: input.amountPaid
      }),
      ...(input.paymentMethod && {
        paymentMethod: input.paymentMethod
      })
    })

    if (!isSuccess && !!errorMessage) {
      showErrorToast(errorMessage)
      return
    }

    await fetchInvoicesData()
    showSuccessToast(translate("successfully_updated"))
  }

  const handleRetryPaymentRequest = async () => {
    if (!currentInvoice?._id || !id) {
      return
    }

    const { isSuccess, errorMessage } = await retryPaymentMutation({
      customerId: id,
      paymentId: currentInvoice?._id,
      paymentMethod: PaymentMethodEnum.CreditCard,
      type: PaymentDetailsEnum.RenewPack,
      ipAdress: ip
    })

    if (!isSuccess && !!errorMessage) {
      showErrorToast(errorMessage)
      return
    }

    await fetchInvoicesData()
    showSuccessToast(translate("successfully_updated"))
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files || event.target.files.length === 0) return

    const uploadedFile = event.target.files[0]

    const fileExtension = uploadedFile.name
      .slice(uploadedFile.name.lastIndexOf("."))
      .toLowerCase()

    const isAcceptableFormat = fileExtension.includes("pdf")
    if (!isAcceptableFormat) return

    await handleUpdateInvoice({
      paymentInvoice: uploadedFile
    })

    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleMarkAsUnPaid = async () => {
    setIsPaymentOpened(false)
    await handleUpdateInvoice({
      status: PaymentStatus.Unpaid
    })
  }
  const handleMarkAsPaid = () => {
    setIsPaymentOpened(true)
    /*  await handleUpdateInvoice({
      status: PaymentStatus.Paid
    }) */
  }

  const handleRetryPayment = () => {
    setIsRetryPaymentOpened(true)
    /*  await handleUpdateInvoice({
      status: PaymentStatus.Paid
    }) */
  }

  const options = useMemo(() => {
    if (currentInvoice?.paymentStatus === "Paid") {
      return [
        {
          headerName: translate("upload_invoice"),
          color: "#000",
          action: handleUploadInvoice
        },
        {
          headerName: translate("mark_as_unpaid"),
          color: DELETE_COLOR,
          action: handleMarkAsUnPaid
        }
      ]
    } else if (currentInvoice?.paymentStatus === "Unpaid") {
      return [
        {
          headerName: translate("upload_invoice"),
          color: "#000",
          action: handleUploadInvoice
        },
        {
          headerName: translate("mark_as_paid"),
          color: WATSAPP_ACTIVE,
          action: handleMarkAsPaid
        },
        {
          headerName: translate("retry_payment"),
          color: ORANGISH,
          action: handleRetryPayment
        }
      ]
    } else {
      return [
        {
          headerName: translate("upload_invoice"),
          color: "#000",
          action: handleUploadInvoice
        },
        {
          headerName: translate("mark_as_paid"),
          color: WATSAPP_ACTIVE,
          action: handleMarkAsPaid
        },
        {
          headerName: translate("retry_payment"),
          color: ORANGISH,
          action: handleRetryPayment
        }
      ]
    }
  }, [currentInvoice?.paymentStatus, translate])

  const fetchInvoicesData = useCallback(async () => {
    if (!id) return false
    try {
      const { isSuccess, data } = await getCustomerInvoicesQuery({
        customerId: id,
        pageNumber: (pagination?.page || 0) + 1,
        pageSize: pagination?.pageSize || 20
      })
      if (isSuccess) {
        setCustomerInvoiceData?.(data)
      }
    } catch (error) {
      console.error("Error fetching data:", error)
      showErrorToast(String(error))
    }
  }, [id, pagination])

  useEffect(() => {
    fetchInvoicesData()
  }, [id, pagination])

  async function fetchIp() {
    try {
      const response = await fetch("https://geolocation-db.com/json/")
      if (!response.ok) {
        throw new Error("Error fetching IP address")
      }

      const data: { IPv4: string } = await response.json()
      setIP(data.IPv4)
    } catch (error) {
      console.error(
        "There has been a problem with your fetch operation:",
        error
      )
    }
  }

  useEffect(() => {
    fetchIp()
  }, [])

  /*  useEffect(() => {
    if (customerInvoiceData && customerInvoiceData.docs) {
      const data = customerInvoiceData.docs
        ?.filter((item) => item !== null)
        .map((item) => {
          return {
            id: item?._id,
            paymentNumber: item?.invoiceNo,
            amount: `$${item?.amountPaid}`,
            paymentMethod: item?.paymentMethod,
            status: item?.paymentStatus,
            invoice: item?.paymentInvoice,
            isDefaultInvoice: item?.isDefaultInvoice,
            date: item?.createdAt
          }
        })

      setRows(data as unknown as InvoiceData[])
    }
  }, [customerInvoiceData?.docs]) */

  // const apiRef = useGridApiRef()

  const formatAccountingDate = (value?: string | null) => {
    if (!value) return ""
    const asNumber = Number(value)
    const date =
      Number.isFinite(asNumber) && String(asNumber) === String(value)
        ? new Date(asNumber)
        : new Date(value)
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("tr-TR")
  }

  // Map the Parasut (accounting) state of an invoice into a label + color.
  // status === "successful" → invoice posted to Parasut; "failed" → sync
  // errored (errorMessage holds the reason); no status but an
  // accountingInvoiceId means it was registered; otherwise nothing was sent.
  const getAccountingState = (invoice: CustomerInvoice) => {
    const status = invoice.accountingInfo?.status
    if (status === "successful") {
      return { label: translate("accounting_sent"), color: WATSAPP_ACTIVE }
    }
    if (status === "failed") {
      return { label: translate("accounting_failed"), color: DELETE_COLOR }
    }
    if (invoice.accountingInvoiceId) {
      return { label: translate("accounting_registered"), color: ORANGISH }
    }
    return { label: translate("accounting_pending"), color: "#9e9e9e" }
  }

  const fieldWidth = 100

  const columns: GridColDef[] = [
    {
      field: "invoiceNo",
      minWidth: 150,
      headerName: translate("payment_number"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("payment_number")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => <Box>{params.value}</Box>,
      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "amountPaid",
      minWidth: fieldWidth,
      headerName: translate("amount"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("amount")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => <Box>{params.value}</Box>,
      disableColumnMenu: true
    },
    {
      field: "paymentMethod",
      minWidth: 150,
      headerName: translate("payment_method"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("payment_method")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const displayValue =
          paymentMethodMap[params.value as keyof typeof paymentMethodMap] ||
          params.value
        return <Box>{displayValue}</Box>
      },
      disableColumnMenu: true
    },
    {
      field: "paymentStatus",
      minWidth: fieldWidth,
      headerName: translate("status"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("status")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            color: params.value === "Paid" ? WATSAPP_ACTIVE : DELETE_COLOR
          }}
        >
          {params.value
            ? params.value.charAt(0).toUpperCase() +
              params.value.slice(1).toLowerCase()
            : ""}
        </Box>
      ),
      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "paymentInvoice",
      minWidth: fieldWidth,
      headerName: translate("invoice"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("invoice")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const isDefaultInvoice = params.row.isDefaultInvoice

        return (
          <IconButton
            sx={{ cursor: "pointer" }}
            onClick={() => window.open(params.value as string, "_blank")}
          >
            <InsertDriveFileIcon
              color={isDefaultInvoice ? "disabled" : "primary"}
            />
          </IconButton>
        )
      },
      disableColumnMenu: true
    },
    {
      field: "accountingInfo",
      minWidth: 160,
      headerName: translate("parasut"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("parasut")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const invoice = params.row as CustomerInvoice
        const { label, color } = getAccountingState(invoice)
        const errorMessage = invoice.accountingInfo?.errorMessage
        const updatedAt = formatAccountingDate(
          invoice.accountingInfo?.updatedAt
        )

        const tooltipContent = (
          <Stack spacing={0.5} sx={{ p: 0.5 }}>
            {invoice.accountingInvoiceId && (
              <Typography variant="caption">
                {translate("accounting_invoice_id")}:{" "}
                {invoice.accountingInvoiceId}
              </Typography>
            )}
            {errorMessage && (
              <Typography variant="caption" sx={{ color: "#ffd2d2" }}>
                {translate("accounting_error")}: {errorMessage}
              </Typography>
            )}
            {updatedAt && (
              <Typography variant="caption">
                {translate("accounting_updated_at")}: {updatedAt}
              </Typography>
            )}
          </Stack>
        )

        const hasDetails = Boolean(
          invoice.accountingInvoiceId || errorMessage || updatedAt
        )

        const chip = (
          <Chip
            size="small"
            label={label}
            sx={{
              backgroundColor: color,
              color: "#fff",
              fontWeight: 500,
              cursor: hasDetails ? "help" : "default"
            }}
          />
        )

        return hasDetails ? (
          <Tooltip title={tooltipContent} arrow>
            {chip}
          </Tooltip>
        ) : (
          chip
        )
      },
      disableColumnMenu: true,
      sortable: false
    },
    {
      field: "createdAt",
      minWidth: fieldWidth,
      headerName: translate("date"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("date")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const date = new Date(parseInt(String(params.value)))
        const formattedDate = date.toLocaleDateString("tr-TR")
        return <Box>{formattedDate}</Box>
      },
      disableColumnMenu: true
    },
    {
      field: "actions",
      minWidth: 100,
      headerName: translate("actions"),
      renderHeader: () => <Box></Box>,
      renderCell: (params: GridRenderCellParams<InvoiceData>) => {
        return (
          <Box>
            <IconButton onClick={(event) => handleOptionClick(event, params)}>
              <MoreVertIcon />
            </IconButton>
            <PopperMenu
              open={Boolean(optionAnchorEl)}
              options={options}
              anchorEl={optionAnchorEl}
              onClose={handlePopClose}
            />
          </Box>
        )
      },
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      align: "right",
      headerAlign: "right"
    }
  ]

  return (
    <Stack sx={styles.containerWidth} py={2}>
      <GridTable
        sortable
        loading={isLoading}
        getRowId={(row) => row._id}
        rows={customerInvoiceData?.docs || []}
        columns={columns}
        rowCount={customerInvoiceData?.totalRecords || 0}
        defaultPageModel={pagination}
        onPaginationModelChange={setPagination}
        disableColumnSelector={true}
      />

      <Stack>
        <input
          type="file"
          id="fileUpload"
          style={{ display: "none" }}
          accept=".pdf"
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </Stack>

      <MarkPaymentAsPaidDialog
        isOpen={isPaymentOpened}
        setClose={() => setIsPaymentOpened(false)}
        onSubmition={handleUpdateInvoice}
        currentInvoice={currentInvoice}
      />

      <RetryPaymentDialog
        isOpen={isRetryPaymentOpened}
        setClose={() => setIsRetryPaymentOpened(false)}
        onSubmition={handleRetryPaymentRequest}
        // currentInvoice={currentInvoice}
      />
    </Stack>
  )
}
